import React, { useEffect, useState } from "react";

import { Icon } from "Components/atoms/Icon";
import { Image } from "Components/atoms/Image";
import { Stops } from "Components/atoms/Stops";
import { Typography } from "Components/atoms/Typography";
import { StopsText } from "../../StopsText";
import {
  FlightDateBlock,
  FlightIconBlock,
  FlightInfoBlock,
  FlightPath,
  FlightStatus,
  SegmentDetails,
  StyledFlightSegment,
} from "./styles";
import { SERPFlightSector } from "shared/types/serpResult";
import {
  convertDateToDDM,
  convertDayOfWeekToText,
  convertDurationToText,
  convertToHHMM,
} from "shared/utils/dates";
import { useFilterContext } from "shared/contexts/FilterContext";

/**
 * Componente FlightSegment
 *
 * Exibe informações detalhadas de um segmento de voo, como horários, aeroportos e paradas.
 *
 * @param {string} airline - Companhia Aérea.
 * @param {string} airlineLogo - Logo da Companhia Aérea.
 * @param {Date} departureDate - Data do voo.
 * @param {number} departureDayOfWeek - Dia da semana do voo.
 * @param {Date} departureTime - Horário de saída do voo.
 * @param {string} departureAirportIata - Código do aeroporto de saída.
 * @param {string} arrivalDate - Data de chegada do voo.
 * @param {Date} arrivalTime - Data de chegada do voo.
 * @param {string} arrivalAirportIata - Código do aeroporto de chegada.
 * @param {number} arrivalDayOfWeek - Dia da semana do voo de chegada.
 * @param {Date} totalDuration - Duração total do voo.
 * @param {number} stops - Quantidade de paradas no voo.
 * @param {number} index - 0 = ida, 1 = volta
 */
export const FlightSector: React.FC<SERPFlightSector> = ({
  airlineLogo,
  airline,
  departureDate,
  departureDayOfWeek,
  departureTime,
  departureAirportIata,
  arrivalDate,
  arrivalTime,
  arrivalAirportIata,
  arrivalDayOfWeek,
  totalDuration,
  stops,
  index,
}) => {
  const { airlines } = useFilterContext();
  const [imageIsValid, setImageIsValid] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    isImageValid(airlineLogo).then((valid) => {
      if (mounted) setImageIsValid(valid);
    });
    return () => {
      mounted = false;
    };
  }, [airlineLogo]);

  const airlineName =
    airlines.find((item) => item.id === airline)?.name ||
    "Companhia desconhecida";

  const isImageValid = (url: string): Promise<boolean> =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });

  return (
    <StyledFlightSegment>
      {imageIsValid === false ? (
        <Typography as="span" weight="normal" color="black" variant="caption">
          {airlineName}
        </Typography>
      ) : (
        <Image
          src={airlineLogo}
          alt="Airline Logo"
          width="85px"
          height="auto"
        />
      )}

      <SegmentDetails>
        <FlightIconBlock>
          {index === 0 ? (
            <Icon
              library="go"
              name="GoArrowUpRight"
              size="small"
              color="darkBlue"
            />
          ) : (
            <Icon
              library="go"
              name="GoArrowDownLeft"
              size="small"
              color="darkBlue"
            />
          )}
        </FlightIconBlock>

        <FlightDateBlock>
          <Typography as="span" weight="bold" color="black">
            {convertDateToDDM(departureDate)}
          </Typography>
          <Typography as="span" variant="label" weight="light">
            {convertDayOfWeekToText(departureDayOfWeek)}
          </Typography>
        </FlightDateBlock>

        <FlightPath>
          <FlightInfoBlock>
            <Typography color="black">
              {convertToHHMM(departureTime)}
            </Typography>
            <Typography variant="caption" color="black">
              {departureAirportIata}
            </Typography>
          </FlightInfoBlock>

          <FlightStatus>
            <StopsText stops={stops} />
            <Stops stops={stops} color="darkGray" />
            <Typography
              as="span"
              variant="label"
              color="darkGray"
              align="center"
            >
              {convertDurationToText(totalDuration)}
            </Typography>
          </FlightStatus>

          <FlightInfoBlock>
            <Typography color="black">{convertToHHMM(arrivalTime)}</Typography>
            <Typography variant="caption" color="black">
              {arrivalAirportIata}
            </Typography>
          </FlightInfoBlock>
        </FlightPath>
      </SegmentDetails>
    </StyledFlightSegment>
  );
};
